import React, { useState, useEffect, FormEvent } from 'react'
import * as Sentry from '@sentry/browser'
import { yupResolver } from '@hookform/resolvers/yup'
import { flowRight as compose, isEmpty } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useResetRecoilState, useSetRecoilState } from 'recoil'
import { useHistory, useParams } from 'react-router-dom'
import { loaderState } from 'recoils/atoms'

import type { Address, CompanyForFindOne, CompanyForFormSubmit } from 'types'
import { Button, PromptGuard, Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'
import {
  CompanyService,
  CompanyForm,
  initialCompanyForm,
  schemaCompanyForm,
  formatForm,
  renderBreadcrumbsEdit,
} from 'features/Company'
import { getPhoneFormatter, getImageFileName } from 'helpers/common'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromiseLoader,
  usePromptForm,
} from 'helpers/hooks'
import { companyEditAddressState } from 'recoils/atoms/companyEditAtom'
import { CompanyForForm, CONTACT_TYPE } from 'types'

function CompanyEditContainer(): JSX.Element {
  const [company, setCompany] = useState<CompanyForFindOne>()
  const setCompanyEditAddress = useSetRecoilState(companyEditAddressState)
  const resetCompanyEditAddress = useResetRecoilState(companyEditAddressState)
  const history = useHistory()
  const params = useParams<{ id: string }>()
  const methods = useForm<CompanyForForm>({
    defaultValues: initialCompanyForm,
    resolver: yupResolver(schemaCompanyForm),
  })
  const [shouldPrompt, setShouldPrompt] = usePromptForm<CompanyForForm>(methods)
  const [isLoadingForm] = useFormSubmitLoader<CompanyForForm>(methods)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [payload, setPayload] = useState<CompanyForFormSubmit>()
  const setLoader = useSetRecoilState(loaderState)

  const promiseLoader = usePromiseLoader()
  const { t } = useTranslation(['layout', 'common'])

  useBreadcrumbs(renderBreadcrumbsEdit(params.id))

  const getCompany = async (id: string) => {
    try {
      const company = await promiseLoader(() => CompanyService.findOneById(id))

      setCompany(company)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)

        await CompanyService.update(params.id, payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
        setLoader(false)
      }
    }
  }

  const handleBack = (): void => {
    history.push(routes.company.path)
  }

  const setData = (data: CompanyForFormSubmit) => {
    const payload = formatForm(data)
    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const setAddressLoading = (loading: boolean) => {
    setIsLoading(loading)
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      handleBack()
    }, 2000)
  }

  useEffect(() => {
    getCompany(params.id)
  }, [])

  useEffect(() => {
    if (!company) {
      return
    }

    const emailContact = company.contacts.find(
      (contact) => contact.type === CONTACT_TYPE.EMAIL,
    )

    const phoneContact = company.contacts.find(
      (contact) => contact.type === CONTACT_TYPE.PHONE,
    )
    const addressData: Address = company.addresses[0]

    methods.reset({
      ...initialCompanyForm,
      originalName: company.name || '',
      originalTaxId: company.taxId || '',
      code: company.code || '',
      isActive: company.isActive,
      name: company.name || '',
      taxId: company.taxId || '',
      email: emailContact?.email || '',
      countryCode: phoneContact?.countryCode || '+66',
      phoneNumber: getPhoneFormatter(`${phoneContact?.number}`) || '',
      logo: company.logo ? getImageFileName(`${company.logo}`) : undefined,
      detail: company.detail || '',
      address: addressData.address || '',
      postalCode: addressData.postalCode || '',
    })

    setCompanyEditAddress(addressData)

    return () => resetCompanyEditAddress()
  }, [company])

  useEffect(() => {
    if (isOpenSuccess) {
      handleOpenSuccess()
    }
  }, [isOpenSuccess])

  return (
    <div className='container__form'>
      <PromptGuard when={shouldPrompt} />
      <FormProvider {...methods}>
        <form onSubmit={beforeSubmit}>
          <CompanyForm setAddressLoading={setAddressLoading} />
          <div className='container__form__actions'>
            <Button variants='secondary' onClick={handleBack}>
              {t('common:cancel.button')}
            </Button>
            <Button type='submit' isLoading={isLoading || isLoadingForm}>
              {t('common:save.button')}
            </Button>
          </div>
        </form>
      </FormProvider>

      {renderConfirmModal()}
      {renderSuccessModal()}
    </div>
  )
}

export default compose(withBackOfficeCms)(CompanyEditContainer)
